/**
 * Eligibilite au remboursement ou taux reduit de la taxe CSPE / Accise sur l'electricite
 * selon le code NAF (secteur d'activite INSEE).
 *
 * Base legale :
 *  - Directive 2003/96/CE (taxation de l'energie)
 *  - Article 158 du Code des douanes (accise sur l'electricite, anciennement CSPE)
 *  - Articles 265 et suivants du Code des douanes (gaz)
 *
 * Niveaux :
 *  - `eligible`   : exoneration totale ou remboursement connu et documente
 *  - `likely`     : taux reduit ou remboursement partiel tres probable selon le secteur
 *  - `none`       : pas d'avantage specifique sur la CSPE
 */

export type CspeLevel = 'eligible' | 'likely' | 'none';

export interface CspeProfile {
  level: CspeLevel;
  /** Libelle court affiche dans l'interface */
  label: string;
  /** Explication juridique / pratique (1-2 phrases) */
  hint: string;
}

const ELIGIBLE_HINT =
  "Votre secteur peut pretendre a une exoneration ou un remboursement partiel/total de la taxe sur l'electricite (accise, anciennement CSPE). Un audit de vos factures est recommande.";
const LIKELY_HINT =
  "Votre secteur peut beneficier d'un taux reduit ou d'un remboursement partiel sur l'accise electricite. Une analyse par notre courtier permettra de chiffrer l'economie.";

/* ────────────────────────────────────────────────────────────────────────────
   Profils par prefixe NAF (du plus specifique au plus general)
   Les prefixes sont testes dans l'ordre — le premier match gagne.
──────────────────────────────────────────────────────────────────────────── */

interface NafCspeEntry {
  /** Prefixe NAF (2, 3, 4 ou 5 caracteres) */
  prefix: string;
  profile: CspeProfile;
}

const NAF_CSPE_ENTRIES: readonly NafCspeEntry[] = [
  // --- Agriculture, sylviculture, peche (01 → 03)
  {
    prefix: '01',
    profile: {
      level: 'eligible',
      label: 'Agriculture',
      hint: "Les exploitants agricoles beneficient d'un taux reduit d'accise sur l'electricite utilisee pour les travaux agricoles (sechage, irrigation, elevage, serres). Remboursement possible sur demande.",
    },
  },
  {
    prefix: '02',
    profile: {
      level: 'eligible',
      label: 'Sylviculture',
      hint: ELIGIBLE_HINT,
    },
  },
  {
    prefix: '03',
    profile: {
      level: 'likely',
      label: 'Peche et aquaculture',
      hint: LIKELY_HINT,
    },
  },

  // --- Industries extractives (05 → 09)
  { prefix: '05', profile: { level: 'eligible', label: 'Extraction de houille', hint: ELIGIBLE_HINT } },
  { prefix: '06', profile: { level: 'eligible', label: "Extraction d'hydrocarbures", hint: ELIGIBLE_HINT } },
  { prefix: '07', profile: { level: 'eligible', label: 'Extraction de minerais metalliques', hint: ELIGIBLE_HINT } },
  { prefix: '08', profile: { level: 'eligible', label: 'Autres industries extractives', hint: ELIGIBLE_HINT } },
  { prefix: '09', profile: { level: 'eligible', label: 'Services de soutien aux industries extractives', hint: ELIGIBLE_HINT } },

  // --- Industries manufacturieres (10 → 33) — electro-intensifs
  {
    prefix: '10',
    profile: {
      level: 'eligible',
      label: 'Industrie alimentaire',
      hint: "Les industries alimentaires (boulangerie industrielle, conserverie, abattoirs...) peuvent beneficier du taux reduit d'accise sur l'electricite utilisee en process.",
    },
  },
  { prefix: '11', profile: { level: 'likely', label: 'Fabrication de boissons', hint: LIKELY_HINT } },
  { prefix: '12', profile: { level: 'likely', label: 'Fabrication de produits a base de tabac', hint: LIKELY_HINT } },
  { prefix: '13', profile: { level: 'eligible', label: 'Fabrication de textiles', hint: ELIGIBLE_HINT } },
  { prefix: '14', profile: { level: 'likely', label: "Industrie de l'habillement", hint: LIKELY_HINT } },
  { prefix: '15', profile: { level: 'likely', label: 'Industrie du cuir et de la chaussure', hint: LIKELY_HINT } },
  { prefix: '16', profile: { level: 'eligible', label: "Travail du bois et fabrication d'articles en bois", hint: ELIGIBLE_HINT } },
  { prefix: '17', profile: { level: 'eligible', label: 'Industrie papier-carton', hint: ELIGIBLE_HINT } },
  { prefix: '18', profile: { level: 'likely', label: 'Imprimerie et reproduction', hint: LIKELY_HINT } },
  {
    prefix: '19',
    profile: {
      level: 'eligible',
      label: 'Cokeification et raffinage',
      hint: "Secteur electro-intensif avec exoneration totale ou remboursement significatif de l'accise.",
    },
  },
  {
    prefix: '20',
    profile: {
      level: 'eligible',
      label: 'Industrie chimique',
      hint: "Secteur electro-intensif — exoneration ou taux reduit important sur l'accise electricite.",
    },
  },
  { prefix: '21', profile: { level: 'eligible', label: 'Industrie pharmaceutique', hint: ELIGIBLE_HINT } },
  { prefix: '22', profile: { level: 'eligible', label: 'Fabrication de produits en caoutchouc et plastique', hint: ELIGIBLE_HINT } },
  {
    prefix: '23',
    profile: {
      level: 'eligible',
      label: 'Fabrication de produits mineraux non metalliques (verre, ciment, ceramique...)',
      hint: "Secteur a fort usage electrique — eligibilite accise favorable.",
    },
  },
  {
    prefix: '24',
    profile: {
      level: 'eligible',
      label: 'Metallurgie',
      hint: "Secteur electro-intensif — taux reduit voire exoneration totale selon le volume consomme.",
    },
  },
  { prefix: '25', profile: { level: 'eligible', label: 'Fabrication de produits metalliques', hint: ELIGIBLE_HINT } },
  { prefix: '26', profile: { level: 'eligible', label: 'Fabrication de produits informatiques et electroniques', hint: ELIGIBLE_HINT } },
  { prefix: '27', profile: { level: 'eligible', label: "Fabrication d'equipements electriques", hint: ELIGIBLE_HINT } },
  { prefix: '28', profile: { level: 'eligible', label: 'Fabrication de machines et equipements', hint: ELIGIBLE_HINT } },
  { prefix: '29', profile: { level: 'eligible', label: 'Industrie automobile', hint: ELIGIBLE_HINT } },
  { prefix: '30', profile: { level: 'eligible', label: "Fabrication d'autres materiels de transport", hint: ELIGIBLE_HINT } },
  { prefix: '31', profile: { level: 'likely', label: 'Fabrication de meubles', hint: LIKELY_HINT } },
  { prefix: '32', profile: { level: 'likely', label: 'Autres industries manufacturieres', hint: LIKELY_HINT } },
  { prefix: '33', profile: { level: 'likely', label: 'Reparation et installation de machines', hint: LIKELY_HINT } },

  // --- Production et distribution d'energie (35)
  {
    prefix: '35',
    profile: {
      level: 'eligible',
      label: "Production et distribution d'energie",
      hint: "L'electricite utilisee pour produire, transporter ou distribuer de l'energie est exoneree d'accise.",
    },
  },

  // --- Eau, assainissement, gestion des dechets (36 → 38)
  { prefix: '36', profile: { level: 'likely', label: "Captage, traitement et distribution d'eau", hint: LIKELY_HINT } },
  { prefix: '37', profile: { level: 'likely', label: 'Collecte et traitement des eaux usees', hint: LIKELY_HINT } },
  { prefix: '38', profile: { level: 'likely', label: 'Collecte, traitement et elimination des dechets', hint: LIKELY_HINT } },

  // --- Transport (49 → 52)
  {
    prefix: '49',
    profile: {
      level: 'eligible',
      label: 'Transports terrestres et transport par conduites',
      hint: "L'electricite utilisee pour la traction ferroviaire et les transports par metro/tram est exoneree d'accise. Les autres transports peuvent beneficier d'un taux reduit.",
    },
  },
  { prefix: '50', profile: { level: 'likely', label: 'Transports par eau', hint: LIKELY_HINT } },
  { prefix: '51', profile: { level: 'likely', label: 'Transports aeriens', hint: LIKELY_HINT } },
  { prefix: '52', profile: { level: 'likely', label: 'Entreposage et services auxiliaires des transports', hint: LIKELY_HINT } },

  // --- Data centers (63.11)
  {
    prefix: '6311',
    profile: {
      level: 'eligible',
      label: 'Centres de donnees (data centers)',
      hint: "Les data centers peuvent beneficier d'un taux reduit d'accise des lors qu'ils atteignent certains seuils d'intensite electrique (directive 2003/96/CE, art. 17).",
    },
  },
];

/* ────────────────────────────────────────────────────────────────────────────
   Resolution NAF → profil CSPE
──────────────────────────────────────────────────────────────────────────── */

/** Normalise un code NAF : supprime les points et espaces, met en majuscule */
function normalizeNaf(code: string | null | undefined): string | null {
  if (!code) return null;
  return code.replace(/[\s.]/g, '').toUpperCase();
}

/** Resout le profil CSPE pour un code NAF donne. Retourne `null` si aucun avantage connu. */
export function resolveCspeEligibility(nafCode: string | null | undefined): CspeProfile | null {
  const code = normalizeNaf(nafCode);
  if (!code) return null;

  // Tester du plus long prefixe (le plus specifique) au plus court
  for (const len of [4, 3, 2]) {
    const prefix = code.slice(0, len);
    const entry = NAF_CSPE_ENTRIES.find((e) => e.prefix === prefix);
    if (entry) return entry.profile;
  }
  return null;
}

/**
 * Retourne vrai si le code NAF donne droit a un avantage CSPE quelconque.
 * Pratique pour le croisement rapide avec l'analyse de facture.
 */
export function isNafCspeEligible(nafCode: string | null | undefined): boolean {
  const p = resolveCspeEligibility(nafCode);
  return p !== null && p.level !== 'none';
}
